import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Avatar, Badge, Empty, Field, Skeleton } from '../components/ui';
import { api, type AuditEvent, type TeamMember } from '../lib/api';
import { actionLabel, actionTone, clockTime, stamp } from '../lib/format';

const ACTIONS = [
  'SEGMENT_START',
  'SEGMENT_STOP',
  'SEGMENT_CREATE',
  'SEGMENT_UPDATE',
  'SEGMENT_DELETE',
  'NOTE_UPSERT',
  'LOGIN',
  'LOGIN_DENIED',
  'LOGOUT',
  'USER_UPDATE',
  'REPORT_EXPORT',
];

export function AuditPage() {
  const [subjectId, setSubjectId] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data: team } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<TeamMember[]>('/admin/users'),
    staleTime: 60_000,
  });

  const params = (cursor?: string) => {
    const p = new URLSearchParams({ limit: '50' });
    if (subjectId) p.set('subjectId', subjectId);
    if (action) p.set('action', action);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (cursor) p.set('cursor', cursor);
    return p.toString();
  };

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useInfiniteQuery({
    queryKey: ['audit', subjectId, action, from, to],
    queryFn: ({ pageParam }) =>
      api.get<{ events: AuditEvent[]; nextCursor: string | null }>(
        `/admin/audit?${params(pageParam as string | undefined)}`,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const events = data?.pages.flatMap((p) => p.events) ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Auditoria</h1>
          <p>
            Cada linha é imutável: o banco recusa alteração e exclusão nesta tabela. Horário do
            servidor, em {Intl.DateTimeFormat().resolvedOptions().timeZone}.
          </p>
        </div>
      </div>

      <section className="card" style={{ marginBottom: 18 }}>
        <div className="card__body">
          <div className="form-row">
            <Field label="Pessoa">
              <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
                <option value="">Todas</option>
                {(team ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Ação">
              <select value={action} onChange={(e) => setAction(e.target.value)}>
                <option value="">Todas</option>
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {actionLabel(a)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="De">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="Até">
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="card">
          <div className="card__body" style={{ display: 'grid', gap: 10 }}>
            <Skeleton height={70} />
            <Skeleton height={70} />
            <Skeleton height={70} />
          </div>
        </div>
      ) : events.length === 0 ? (
        <div className="card">
          <Empty title="Nenhum evento com esses filtros" />
        </div>
      ) : (
        <section className="card">
          <div className="card__body--tight">
            {events.map((e) => (
              <EventRow key={e.id} event={e} />
            ))}
          </div>
        </section>
      )}

      {hasNextPage ? (
        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <button className="btn" disabled={isFetching} onClick={() => void fetchNextPage()}>
            {isFetching ? 'Carregando…' : 'Carregar mais'}
          </button>
        </div>
      ) : null}
    </>
  );
}

function EventRow({ event }: { event: AuditEvent }) {
  const [open, setOpen] = useState(false);
  const hasDiff = Boolean(event.before || event.after);

  return (
    <div style={{ borderBottom: '1px solid var(--line)' }}>
      <button
        onClick={() => hasDiff && setOpen(!open)}
        aria-expanded={open}
        style={{
          all: 'unset',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          gap: 12,
          alignItems: 'center',
          width: '100%',
          padding: '14px 20px',
          cursor: hasDiff ? 'pointer' : 'default',
          boxSizing: 'border-box',
        }}
      >
        <Avatar name={event.actor.name} src={event.actor.avatarUrl} size={34} />

        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Badge tone={actionTone(event.action)}>{actionLabel(event.action)}</Badge>
            <strong style={{ fontSize: '0.92rem' }}>{event.actor.name}</strong>
            {event.subject && event.subject.email !== event.actor.email ? (
              <span style={{ fontSize: '0.86rem', color: 'var(--muted-2)' }}>
                sobre {event.subject.name}
              </span>
            ) : null}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--faint)', marginTop: 3 }}>
            {event.reason ? `"${event.reason}" · ` : ''}
            {event.ip ? `IP ${event.ip} · ` : ''}
            {event.requestId ? `req ${event.requestId.slice(0, 8)}` : ''}
          </div>
        </div>

        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div className="tnum" style={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {stamp(event.at).slice(0, 10)}
          </div>
          <div className="tnum" style={{ fontSize: '0.82rem', color: 'var(--muted-2)' }}>
            {stamp(event.at).slice(12)}
          </div>
        </div>
      </button>

      {open ? (
        <div style={{ padding: '0 20px 16px' }}>
          <Diff before={event.before} after={event.after} />
        </div>
      ) : null}
    </div>
  );
}

const FIELD_LABELS: Record<string, string> = {
  startedAt: 'Entrada',
  endedAt: 'Saída',
  kind: 'Tipo',
  workDate: 'Dia',
  description: 'Descrição',
  source: 'Origem',
  version: 'Versão',
  deletedAt: 'Excluído em',
  content: 'Conteúdo',
  role: 'Papel',
  status: 'Situação',
  dailyTargetMinutes: 'Meta diária (min)',
  workDays: 'Dias esperados',
  name: 'Nome',
  email: 'E-mail',
};

function pretty(key: string, value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return key === 'deletedAt' ? stamp(value) : clockTime(value);
  }
  return String(value);
}

/** Mostra só as chaves que existem, marcando em cor as que mudaram. */
function Diff({ before, after }: { before: unknown; after: unknown }) {
  const b = (before ?? {}) as Record<string, unknown>;
  const a = (after ?? {}) as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].filter(
    (k) => k !== 'id' && k !== 'userId',
  );

  if (keys.length === 0) return null;

  return (
    <div className="diff">
      <div className="diff__col">
        <h4>Antes</h4>
        {before ? (
          <dl>
            {keys.map((k) => (
              <Row key={k} k={k} v={b[k]} changed={JSON.stringify(b[k]) !== JSON.stringify(a[k])} />
            ))}
          </dl>
        ) : (
          <span style={{ color: 'var(--faint)' }}>Não existia</span>
        )}
      </div>
      <div className="diff__col">
        <h4>Depois</h4>
        {after ? (
          <dl>
            {keys.map((k) => (
              <Row key={k} k={k} v={a[k]} changed={JSON.stringify(b[k]) !== JSON.stringify(a[k])} />
            ))}
          </dl>
        ) : (
          <span style={{ color: 'var(--faint)' }}>Removido</span>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, changed }: { k: string; v: unknown; changed: boolean }) {
  return (
    <>
      <dt>{FIELD_LABELS[k] ?? k}</dt>
      <dd className={changed ? 'changed' : undefined}>{pretty(k, v)}</dd>
    </>
  );
}
